import { ScrollTrigger } from 'gsap/ScrollTrigger'

const DEFAULT_AUTO_REFRESH_EVENTS = 'visibilitychange,DOMContentLoaded,load,resize'

/** @type {WeakMap<Element, () => void>} */
const prepareByElement = new WeakMap()

let protoPatched = false
/** @type {((...args: any[]) => any) | null} */
let nativeElementRequestFullscreen = null

const ensurePrototypePatch = () => {
  if (protoPatched) {
    return
  }

  const proto = Element.prototype
  if (typeof proto.requestFullscreen !== 'function') {
    return
  }

  nativeElementRequestFullscreen = proto.requestFullscreen
  proto.requestFullscreen = function pinnedVideoFullscreenGate (...args) {
    const prepare = prepareByElement.get(this)
    if (typeof prepare === 'function') {
      prepare()
    }
    return nativeElementRequestFullscreen.apply(this, args)
  }
  protoPatched = true
}

const isVideoFullscreenElement = (video) => {
  if (!video) {
    return false
  }

  const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement
  return fullscreenElement === video || Boolean(video.webkitDisplayingFullscreen)
}

const preventDefault = (event) => {
  event.preventDefault()
}

/**
 * Keep native <video> fullscreen stable inside GSAP ScrollTrigger pins.
 *
 * Root cause: fullscreen changes the viewport → ScrollTrigger.refresh / pin
 * scrub updates scroll the page → browser exits fullscreen immediately.
 *
 * Fix (only while fullscreen is active — does not change play layout):
 * 1. disable every ScrollTrigger without revert (no pin-spacer jump)
 * 2. no-op ScrollTrigger.refresh (blocks HardwareCalloutScroller resize refresh)
 * 3. hard-lock scroll position
 * 4. clear transform on the how-section targets
 *
 * Do NOT kill pins or reparent the video — that breaks normal playback layout.
 *
 * @param {HTMLVideoElement | null | undefined} video
 * @param {{
 *   getScrollTrigger?: () => { enabled?: boolean, disable?: Function, enable?: Function } | null | undefined,
 *   getSettleTargets?: () => Array<HTMLElement | null | undefined>,
 * }} [options]
 * @returns {() => void} cleanup
 */
export function bindPinnedVideoFullscreen(video, options = {}) {
  if (!video) {
    return () => {}
  }

  const { getSettleTargets } = options

  let prepared = false
  let lockedScrollY = 0
  let originalRefresh = null
  /** @type {Array<{ enabled?: boolean, disable?: Function, enable?: Function }>} */
  let disabledTriggers = []
  const settledInlineStyles = []

  const settleTargetsVisible = () => {
    settledInlineStyles.length = 0
    const targets = typeof getSettleTargets === 'function' ? getSettleTargets() : []

    targets.forEach((el) => {
      if (!el) {
        return
      }

      settledInlineStyles.push({
        el,
        transform: el.style.transform,
        willChange: el.style.willChange,
        opacity: el.style.opacity,
        visibility: el.style.visibility,
        pointerEvents: el.style.pointerEvents,
        filter: el.style.filter
      })

      el.style.setProperty('transform', 'none', 'important')
      el.style.setProperty('filter', 'none', 'important')
      el.style.setProperty('will-change', 'auto')
      el.style.setProperty('opacity', '1')
      el.style.setProperty('visibility', 'visible')
      el.style.setProperty('pointer-events', 'auto')
    })
  }

  const restoreSettledInlineStyles = () => {
    settledInlineStyles.forEach((entry) => {
      const { el } = entry
      if (!el) {
        return
      }

      el.style.removeProperty('transform')
      el.style.removeProperty('filter')
      el.style.removeProperty('will-change')
      el.style.removeProperty('opacity')
      el.style.removeProperty('visibility')
      el.style.removeProperty('pointer-events')

      if (entry.transform) el.style.transform = entry.transform
      if (entry.filter) el.style.filter = entry.filter
      if (entry.willChange) el.style.willChange = entry.willChange
      if (entry.opacity) el.style.opacity = entry.opacity
      if (entry.visibility) el.style.visibility = entry.visibility
      if (entry.pointerEvents) el.style.pointerEvents = entry.pointerEvents
    })

    settledInlineStyles.length = 0
  }

  const lockScrollPosition = () => {
    if ((window.pageYOffset || window.scrollY || 0) !== lockedScrollY) {
      window.scrollTo(0, lockedScrollY)
    }
  }

  const onScrollLock = () => {
    if (prepared) {
      lockScrollPosition()
    }
  }

  const prepare = () => {
    if (prepared) {
      return
    }

    prepared = true
    lockedScrollY = window.pageYOffset || window.scrollY || 0

    // Fullscreen resizes the viewport; stop ST auto listeners.
    ScrollTrigger.config({ autoRefreshEvents: 'none' })

    // Other components call ScrollTrigger.refresh() on resize (e.g. hardware
    // scroller). That re-pins and scrolls the page, which aborts fullscreen.
    originalRefresh = ScrollTrigger.refresh
    ScrollTrigger.refresh = () => ScrollTrigger

    // Freeze every live trigger without reverting layout (no spacer removal).
    disabledTriggers = []
    ScrollTrigger.getAll().forEach((trigger) => {
      if (trigger?.enabled) {
        disabledTriggers.push(trigger)
        trigger.disable(false)
      }
    })

    settleTargetsVisible()

    document.documentElement.style.setProperty('overflow', 'hidden')
    document.body.style.setProperty('overflow', 'hidden')
    lockScrollPosition()

    window.addEventListener('scroll', onScrollLock, true)
    window.addEventListener('wheel', preventDefault, { passive: false, capture: true })
    window.addEventListener('touchmove', preventDefault, { passive: false, capture: true })
  }

  const restore = () => {
    if (!prepared) {
      return
    }

    prepared = false

    window.removeEventListener('scroll', onScrollLock, true)
    window.removeEventListener('wheel', preventDefault, true)
    window.removeEventListener('touchmove', preventDefault, true)

    document.documentElement.style.removeProperty('overflow')
    document.body.style.removeProperty('overflow')

    restoreSettledInlineStyles()

    disabledTriggers.forEach((trigger) => {
      try {
        if (trigger && !trigger.enabled && typeof trigger.enable === 'function') {
          trigger.enable()
        }
      } catch {
        // Trigger may have been killed by route change / unmount.
      }
    })
    disabledTriggers = []

    if (typeof originalRefresh === 'function') {
      ScrollTrigger.refresh = originalRefresh
    }
    originalRefresh = null

    ScrollTrigger.config({ autoRefreshEvents: DEFAULT_AUTO_REFRESH_EVENTS })

    // Keep scroll where the user was, then refresh after FS exit settles.
    window.scrollTo(0, lockedScrollY)
    requestAnimationFrame(() => {
      window.scrollTo(0, lockedScrollY)
      requestAnimationFrame(() => {
        window.scrollTo(0, lockedScrollY)
        if (typeof ScrollTrigger.refresh === 'function') {
          ScrollTrigger.refresh()
        }
      })
    })
  }

  // Instance overrides (some UAs call these from media controls).
  const originalRequestFullscreen = typeof video.requestFullscreen === 'function'
    ? video.requestFullscreen.bind(video)
    : null
  const originalWebkitRequestFullscreen = typeof video.webkitRequestFullscreen === 'function'
    ? video.webkitRequestFullscreen.bind(video)
    : null
  const originalWebkitEnterFullscreen = typeof video.webkitEnterFullscreen === 'function'
    ? video.webkitEnterFullscreen.bind(video)
    : null

  const runFullscreenRequest = (requestFn, args) => {
    prepare()
    const result = requestFn(...args)

    if (result && typeof result.then === 'function') {
      return result.catch((error) => {
        restore()
        throw error
      })
    }

    return result
  }

  if (originalRequestFullscreen) {
    video.requestFullscreen = (...args) => runFullscreenRequest(originalRequestFullscreen, args)
  }

  if (originalWebkitRequestFullscreen) {
    video.webkitRequestFullscreen = (...args) => runFullscreenRequest(originalWebkitRequestFullscreen, args)
  }

  if (originalWebkitEnterFullscreen) {
    video.webkitEnterFullscreen = (...args) => runFullscreenRequest(originalWebkitEnterFullscreen, args)
  }

  // Prototype path: native media controls often call Element.prototype.requestFullscreen.
  ensurePrototypePatch()
  prepareByElement.set(video, prepare)

  const onFullscreenChange = () => {
    if (isVideoFullscreenElement(video)) {
      prepare()
      lockScrollPosition()
      return
    }

    restore()
  }

  const onWebkitBeginFullscreen = () => {
    prepare()
    lockScrollPosition()
  }

  const onWebkitEndFullscreen = () => {
    restore()
  }

  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('webkitfullscreenchange', onFullscreenChange)
  video.addEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen)
  video.addEventListener('webkitendfullscreen', onWebkitEndFullscreen)

  return () => {
    prepareByElement.delete(video)

    if (originalRequestFullscreen) {
      video.requestFullscreen = originalRequestFullscreen
    }

    if (originalWebkitRequestFullscreen) {
      video.webkitRequestFullscreen = originalWebkitRequestFullscreen
    }

    if (originalWebkitEnterFullscreen) {
      video.webkitEnterFullscreen = originalWebkitEnterFullscreen
    }

    document.removeEventListener('fullscreenchange', onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
    video.removeEventListener('webkitbeginfullscreen', onWebkitBeginFullscreen)
    video.removeEventListener('webkitendfullscreen', onWebkitEndFullscreen)

    restore()
  }
}
